import { Stack } from '@chakra-ui/react'
import type { GeneralParamsDto, InsuranceParamsTravelDto, PolicyHolderDto, TravelDto } from 'BrokerTsLib/src/dtos'
import { InitialBaseParams, InitialGeneralParamsValues, InitialPersonAdditionalData, InitialPolicyHolderValues, InitialTravelAddonValues, InitialTravelValues } from 'BrokerTsLib/src/dtos'
import { Product } from 'BrokerTsLib/src/enums'
import * as global from 'brokertslib/src/helpers/global'
import { useInsuranceParamsFormPersistance } from 'BrokerTsLib/src/hooks'
import { FormProvider, useForm, yupResolver } from 'BrokerTsLib/src/rhf'
import { formatDate } from 'BrokerTsLib/src/Utils/formatPrice'
import { PolicyHolderTravelValidationSchema, TravelValidationObject } from 'BrokerTsLib/src/validation'
import cloneDeep from 'lodash/cloneDeep'
import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import * as yup from 'yup'

import { CustomWaypoint } from '@/components/CustomWaypoint'
import ErrorSummary from '@/components/ErrorSummary'
import { PageSection } from '@/components/PageSection'
import { UnsavedFormModal } from '@/components/UnsavedFormModal'
import { useInsuranceParams } from '@/context/InsuranceParamsProvider'
import { useSidebar } from '@/context/SidebarContext'
import { useSaveInsuranceParams, useUpdateLastWizardStep } from '@/hooks/useWizard'
import type { PageSection as Section } from '@/layouts/components/Sidebar'

import { Insurees } from './containers'
import { Parameters } from './containers/Parameters'
import { PolicyHolder } from './containers/PolicyHolder'

export const schema = yup.object().shape(
  {
    travel: yup.object().shape({
      ...TravelValidationObject,
      insurers: yup.array().nullable().optional(),
      range: yup.number().nullable().optional()
    }),
    policyHolder: PolicyHolderTravelValidationSchema,
  }
) as any


interface Step1FormProps {
  data?: InsuranceParamsTravelDto
}
const defaultValues: InsuranceParamsTravelDto = {
  nextPage: 'next',
  generalParams: InitialGeneralParamsValues,
  policyHolder: InitialPolicyHolderValues,
  travel: { ...InitialTravelValues, insurees: [{ isForeigner: false, isPolicyHolder: false, firstName: '', lastName: '', birthdate: null }] },
  addons: InitialTravelAddonValues,
  baseParams: InitialBaseParams
}

export const Step1Sections = {
  Parameters: 'parameters',
  PolicyHolder: 'policyHolder',
  Insurees: 'insurees'
}
export type Step1Sections = typeof Step1Sections[keyof typeof Step1Sections]

export const transformInsuranceParamsToFormValues = (values: InsuranceParamsTravelDto) => {
  return {
    ...values,
    generalParams: transformGeneralParamsDto(values.generalParams),
    policyHolder: transformPolicyHolderDto(values.policyHolder),
    travel: transformTravelDto(values.travel)
  } as InsuranceParamsTravelDto
}

export const transformPolicyHolderDto = (policyHolder: PolicyHolderDto) => {
  if (policyHolder.person == null) policyHolder.person = { ...InitialPersonAdditionalData }

  return policyHolder
}

export const transformGeneralParamsDto = (generalParams: GeneralParamsDto) => {
  generalParams.insuranceStartDate = transformDate(generalParams.insuranceStartDate)

  return generalParams
}

export const transformTravelDto = (travel: TravelDto) => {
  travel.insuranceEndDate = transformDate(travel.insuranceEndDate)

  travel.insurees?.forEach(x => x.uid = global.newUid())

  return travel
}

export const transformDate = (date: any) => date ? new Date(date) : undefined

export const Step1 = ({ data = defaultValues }: Step1FormProps) => {
  const navigate = useNavigate()
  const { triggerNewInsurance, clearTriggerNewInsurance, insuranceParamsId, refetchInsuranceParams, insuranceParams } = useInsuranceParams()
  const { mutateAsync: saveInsuranceParams } = useSaveInsuranceParams()
  const { mutateAsync: updateStep } = useUpdateLastWizardStep()

  const methods = useForm<InsuranceParamsTravelDto>({
    defaultValues: transformInsuranceParamsToFormValues(data),
    resolver: yupResolver(schema),
    mode: 'onBlur'
  })

  const {
    formState: { errors },
    handleSubmit,
    setValue,
    reset,
    setError
  } = methods

  const insuranceParamsFormPersistance = useInsuranceParamsFormPersistance(Product.TravelInsurance, 1, insuranceParamsId, methods)

  const submitForm = handleSubmit((data) => {
    const values = cloneDeep(data)

    values.generalParams.insuranceStartDate = formatDate(values.generalParams.insuranceStartDate)
    values.policyHolder.person.birthday = formatDate(values.policyHolder.person.birthday)
    values.travel.insuranceEndDate = formatDate(values.travel.insuranceEndDate)
    values.travel.insurees?.forEach(x => x.birthdate = formatDate(x.birthdate!))

    return saveInsuranceParams(values)
      .then(async ({ data: paramsId }) => {
        reset(data)
        insuranceParamsFormPersistance.clear()

        if ((insuranceParams?.generalParams.lastWizardStep ?? 1) <= 1)
          await updateStep({ paramsId: paramsId, step: 2 })

        if (insuranceParamsId)
          refetchInsuranceParams()

        // aby se stihl resetovat formular a smazal local storage pred navigaci (jinak se zobrazuje okno s neulozenymi daty)
        setTimeout(() => {
          navigate(`/travel/steps/2/${paramsId}`)
        }, 0)

      })
      .catch((err) => {
        if (!(err.response && err.response.status === 400 && err.response.data))
          return

        const data = err.response.data

        // zobrazeni serverove validace
        Object.keys(data).forEach((x: any) => {
          setError(global.toCamelCase(x), { message: data[x].join('. ') })
        })
      })
  })

  // pro pripad kdyz se z K1 klidne na 'Nové pojištění', tak se musi formular resetnout. Jinak nedochazelo k rerenderu
  useEffect(() => {
    if (triggerNewInsurance)
      reset(defaultValues)

    clearTriggerNewInsurance()
  }, [triggerNewInsurance])

  const { pageSections, setPageSections } = useSidebar()

  useEffect(() => {
    let i = 1
    const array: Section[] = [
      {
        id: Step1Sections.Parameters,
        sectionTitle: 'Parametry pojištění',
        index: i,
        children: (
          <Parameters
            number={i++}
            sectionTitle="Parametry pojištění"
          />
        )
      },
      {
        id: Step1Sections.PolicyHolder,
        sectionTitle: 'Pojistník',
        index: i,
        children: (
          <PolicyHolder
            number={i++}
            sectionTitle="Pojistník"
          />
        )
      },
      {
        id: Step1Sections.Insurees,
        sectionTitle: 'Pojištěné osoby',
        index: i,
        children: (
          <Insurees
            number={i++}
            sectionTitle="Pojištěné osoby"
            onContinue={() => {
              setValue('nextPage', 'next')

              return submitForm()
            }}
          />
        )
      }
    ]
    setPageSections(array)
  }, [])

  return (
    <FormProvider {...methods}>
      <form onSubmit={() => { }}>
        <Stack gap={8}>
          {pageSections.map(x => (
            <CustomWaypoint key={x.id} sectionId={x.id}>
              <PageSection id={x.id}>
                {x.children}
              </PageSection>
            </CustomWaypoint>
          ))}

          <ErrorSummary errors={errors} />

          <UnsavedFormModal {...insuranceParamsFormPersistance} />
        </Stack>
      </form>
    </FormProvider>
  )
}
